import { useState } from "react";
import { Pencil } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import BracketEditSlotDialog from "./BracketEditSlotDialog";

const ROUND_ORDER = ["group_stage", "round_of_16", "quarter_final", "semi_final", "final"];
const ROUND_LABELS = {
  group_stage: "Group Stage",
  round_of_16: "Round of 16",
  quarter_final: "Quarter Finals",
  semi_final: "Semi Finals",
  final: "Final",
};

/**
 * Knockout bracket display — groups matches by round and paints winners.
 * Pure presentation, no data fetching.
 *
 * For empty/TBD slots, we prefer the backend-supplied
 * `playerN_name = "Winner: A vs B"` label (set by `enrich_matches` when the
 * match has `feeder_match_ids` but no resolved player yet).
 *
 * Admins (when `canEdit && tournamentId` are passed) get an inline pencil
 * button on each scheduled match's player slot to swap the player.
 */
export default function BracketView({
  matches, players, canEdit = false, tournamentId, onChanged,
}) {
  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p; });

  const [editing, setEditing] = useState(null); // {match, slot}

  const rounds = {};
  matches.forEach((m) => {
    const r = m.round || "unknown";
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(m);
  });

  const sortedRounds = Object.keys(rounds).sort((a, b) => {
    const ai = ROUND_ORDER.indexOf(a);
    const bi = ROUND_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <div className="space-y-6" data-testid="bracket-view">
      {sortedRounds.map((round) => (
        <div key={round}>
          <h3 className="font-heading font-semibold mb-3 text-primary">
            {ROUND_LABELS[round] || round.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rounds[round].map((m) => (
              <BracketMatchCard
                key={m.id}
                match={m}
                playerMap={playerMap}
                canEdit={canEdit}
                onEditSlot={(slot) => setEditing({ match: m, slot })}
              />
            ))}
          </div>
        </div>
      ))}

      <BracketEditSlotDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        tournamentId={tournamentId}
        match={editing?.match}
        slot={editing?.slot}
        players={players}
        onChanged={() => { setEditing(null); onChanged?.(); }}
      />
    </div>
  );
}

function BracketMatchCard({ match: m, playerMap, canEdit, onEditSlot }) {
  const isLive = m.status === "live";
  const isScheduled = m.status === "scheduled";
  const slotEditable = (slot) =>
    canEdit && isScheduled && m.round !== "group_stage";

  // Prefer backend-resolved labels (which include "Winner: A vs B" for TBD
  // slots whose feeder match is still pending). Fall back to playerMap for
  // legacy callers that didn't run through enrich_matches.
  const slotLabel = (slot) => {
    const id = m[`${slot}_id`];
    if (id) {
      return m[`${slot}_name`] || playerMap[id]?.name || "TBD";
    }
    return m[`${slot}_name`] || "TBD";
  };
  const slotPending = (slot) => !!m[`${slot}_is_pending`] && !m[`${slot}_id`];

  return (
    <div className={`p-3 rounded-lg border ${isLive ? "border-primary/50 bg-primary/5" : "border-border"}`}>
      <div className="flex items-center justify-between">
        <BracketSlot
          name={slotLabel("player1")}
          isWinner={m.winner_id === m.player1_id}
          isPending={slotPending("player1")}
          editable={slotEditable("player1")}
          onEdit={() => onEditSlot("player1")}
          align="left"
        />
        <div className="px-3 text-center">
          {m.status === "completed" || isLive ? (
            <span className="font-heading font-bold">{m.player1_score || 0} - {m.player2_score || 0}</span>
          ) : (
            <span className="text-xs text-muted-foreground">vs</span>
          )}
        </div>
        <BracketSlot
          name={slotLabel("player2")}
          isWinner={m.winner_id === m.player2_id}
          isPending={slotPending("player2")}
          editable={slotEditable("player2")}
          onEdit={() => onEditSlot("player2")}
          align="right"
        />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
        <span>{m.scheduled_date || ""} {m.scheduled_time || ""}</span>
        <Badge
          variant="outline"
          className={`text-[10px] ${
            isLive ? "text-primary border-primary/30"
            : m.status === "completed" ? "text-chart-3 border-chart-3/30" : ""
          }`}
        >
          {isLive ? "LIVE" : m.status}
        </Badge>
      </div>
    </div>
  );
}

function BracketSlot({ name, isWinner, isPending, editable, onEdit, align }) {
  const justify = align === "right" ? "justify-end" : "justify-start";
  const textAlign = align === "right" ? "text-right" : "text-left";
  // Pending labels ("Winner: A vs B") are italicised + dimmer to distinguish
  // them from confirmed players.
  const slotClass = isPending
    ? "italic text-muted-foreground"
    : isWinner ? "text-primary" : "";
  return (
    <div className={`flex-1 flex items-center gap-1 ${justify} min-w-0`}>
      {align === "right" && editable && (
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 text-muted-foreground hover:text-secondary"
          onClick={onEdit}
          title="Edit slot"
          data-testid="bracket-edit-slot-pencil"
        >
          <Pencil className="w-3 h-3" weight="fill" />
        </Button>
      )}
      <span
        className={`text-sm font-medium truncate ${textAlign} ${slotClass}`}
        title={name}
      >
        {name}
      </span>
      {align === "left" && editable && (
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 text-muted-foreground hover:text-secondary"
          onClick={onEdit}
          title="Edit slot"
          data-testid="bracket-edit-slot-pencil"
        >
          <Pencil className="w-3 h-3" weight="fill" />
        </Button>
      )}
    </div>
  );
}
